import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useMediaQuery } from '@mui/material';
import Loader from '../../../common/loader';
import {
  assignDealBankers,
  assignDealBankersWithInvites,
  deleteCustomerLoanRequest,
  getAllBankerRegistration,
  getAllCustomerLoanRequest,
  updateLoanClosing,
} from '../../../services/customer.service';
import { commonMessage } from '../../../constants/message.constant';
import { Customer, Deal } from '../../../validation/customer.validation';
import AssignBankerDialog, { Banker } from './assignedBankerDialog';
import ConfirmDeleteDialog from '../../../common/confirmDeleteAlert';
import EditLoanDeal from './EditLoanDeal';
import TermSheetView from './TermSheetView';
import { getLoginUserData } from '../../../services/account.service';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, parse } from "date-fns";
import DealsMobileCardView from './DealsMobileCardView';
import DealsDesktopTableView from './DealsDesktopTableView';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import TaskFormDialog from '../../business/tasks/TaskFormDialog';
import { useLocation, useNavigate } from 'react-router-dom';

const DealsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [orderBy, setOrderBy] = useState<keyof Deal>('loanName');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningDeal, setAssigningDeal] = useState<Deal | null>(null);
  const [bankers, setBankers] = useState<Banker[]>([]);
  const [savingAssignment, setSavingAssignment] = useState(false);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Deal | null>(null);

  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [loggedInUser, setLoggedInUser] = useState<Customer | null>(null);

  const [viewingDeal, setViewingDeal] = useState<Deal | null>(null);
  const [viewingBankerId, setViewingBankerId] = useState<string | null>(null);

  const [closingDeal, setClosingDeal] = useState<Deal | null>(null);
  const [closingDate, setClosingDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [savingClosing, setSavingClosing] = useState(false);
  const [taskModal, setTaskModal] = useState({
    open: false,
    deal: null as Deal | null,
  });

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sortingLoading, setSortingLoading] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isMobile = useMediaQuery('(max-width:600px)');

  const loadDeals = async (search: string = '') => {
    try {
      setLoading(true);
      const response = await getAllCustomerLoanRequest(search);
      setDeals(response);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setSnackbar({
        open: true,
        message:
          error?.message || error?.response?.data?.message || commonMessage.unknownError,
        severity: 'error',
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      loadDeals();
      const response = await getAllBankerRegistration();
      setBankers(response);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const user = await getLoginUserData();
        setLoggedInUser(user);
      } catch {
        console.error('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    const openDealId = (location.state as any)?.openDealId;
    if (!openDealId || !deals.length || openEditModal) return;

    const deal = deals.find((item) => item.id === openDealId);
    if (deal) {
      setSelectedDeal(deal);
      setOpenEditModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [deals, location.pathname, location.state, navigate, openEditModal]);

  const sortedDeals = useMemo(() => {
    return [...deals].sort((a, b) => {
      const aValue: unknown = a[orderBy as keyof Deal];
      const bValue: unknown = b[orderBy as keyof Deal];

      if (aValue === undefined || aValue === null) return order === 'asc' ? 1 : -1;
      if (bValue === undefined || bValue === null) return order === 'asc' ? -1 : 1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [deals, orderBy, order]);

  const handleSort = (columnId: keyof Deal) => {
    setSortingLoading(true);
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
    setTimeout(() => setSortingLoading(false), 100);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      loadDeals(query);
    }, 500);
  };

  const onDelete = (id?: string) => {
    if (!id) return;
    const deal = deals.find((d) => d.id === id);
    if (deal) {
      setDeleteTarget(deal);
      setOpenDeleteDialog(true);
    }
  };

  const handleAssignClick = (deal: Deal) => {
    setAssigningDeal(deal);
    setAssignDialogOpen(true);
  };

  const handleAddTaskClick = (deal: Deal) => {
    setTaskModal({ open: true, deal });
  };

  const handleAssignDeal = async (
    dealId: string,
    bankerIds: string[],
    importedBankerIds: string[],
    isShareCustomerDetails: boolean
  ) => {
    setSavingAssignment(true);
    try {
      const hasImportedBankers = importedBankerIds.length > 0;
      const response = hasImportedBankers
        ? await assignDealBankersWithInvites(
            dealId,
            bankerIds,
            importedBankerIds,
            isShareCustomerDetails,
          )
        : await assignDealBankers(
            dealId,
            bankerIds,
            isShareCustomerDetails,
          );
      await loadDeals(searchQuery);
      const result = hasImportedBankers ? response?.data : null;
      const missingNames = result?.missingEmailBankers
        ?.map((banker: { name: string }) => banker.name)
        .join(", ");
      const failedNames = result?.failedImportedBankers
        ?.map((banker: { name: string }) => banker.name)
        .join(", ");

      setAssignDialogOpen(false);
      setAssigningDeal(null);
      setSnackbar({
        open: true,
        message:
          missingNames
            ? `Bankers assigned. Email not found for ${missingNames}.`
            : failedNames
              ? `Bankers assigned, but invitation failed for ${failedNames}.`
              : 'Bankers assigned successfully!',
        severity: missingNames || failedNames ? 'error' : 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err?.message || 'Assignment failed',
        severity: 'error',
      });
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      await deleteCustomerLoanRequest(deleteTarget.id);
      setDeals((prev) => prev.filter((d) => d.id !== deleteTarget.id));
      setSnackbar({ open: true, message: 'Deal deleted successfully', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.message || 'Delete failed', severity: 'error' });
    }
    setOpenDeleteDialog(false);
    setDeleteTarget(null);
  };

  const handleRowExpand = (dealId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(dealId)) newExpanded.delete(dealId);
    else newExpanded.add(dealId);
    setExpandedRows(newExpanded);
  };

  const openClosingDialog = (deal: Deal) => {
    setClosingDeal(deal);
    setClosingDate(
      deal.closingDate ? deal.closingDate.slice(0, 10) : new Date().toISOString().slice(0, 10)
    );
  };

  const closeClosingDialog = () => {
    if (savingClosing) return;
    setClosingDeal(null);
  };

  const saveClosingDate = async () => {
    if (!closingDeal?.id) return;

    try {
      setSavingClosing(true);
      await updateLoanClosing(closingDeal.id, { closingDate });
      await loadDeals(searchQuery);
      setSnackbar({ open: true, message: 'Closing date saved', severity: 'success' });
      setClosingDeal(null);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message || error?.message || 'Failed to update closing',
        severity: 'error',
      });
    } finally {
      setSavingClosing(false);
    }
  };

  return (
    <div className="p-2 bg-gray-50 min-h-screen">
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "center", md: "center" },
          justifyContent: { xs: "center", md: "space-between" },
          gap: { xs: 2, md: 0 },
          mb: 6,
          p: 2,
          bgcolor: "white",
          borderRadius: 2,
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box>
          <Box
            sx={{
              fontWeight: 700,
              color: "#333",
              textAlign: { xs: "center", md: "left" },
              fontSize: 22,
            }}
          >
            Loan Deals Management
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
            alignItems: "center",
          }}
        >
          <TextField
            label="Search Deals"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              minWidth: { xs: 200, sm: 250 },
              width: { xs: "100%", sm: "auto" },
            }}
          />
        </Box>
      </Box>

      {loading || sortingLoading ? (
        <Loader />
      ) : isMobile ? (
        <DealsMobileCardView
          deals={sortedDeals}
          loggedInUser={loggedInUser}
          expandedRows={expandedRows}
          onToggleExpand={handleRowExpand}
          onAssignClick={handleAssignClick}
          onAddTaskClick={handleAddTaskClick}
          onEditClick={(deal) => {
            setSelectedDeal(deal);
            setOpenEditModal(true);
          }}
          onDeleteClick={onDelete}
          onViewTermSheet={(deal, bankerId) => {
            setViewingDeal(deal);
            setViewingBankerId(bankerId);
          }}
          onClosingClick={openClosingDialog}
        />
      ) : (
        <DealsDesktopTableView
          deals={sortedDeals}
          loggedInUser={loggedInUser}
          orderBy={orderBy}
          order={order}
          onSort={handleSort}
          expandedRows={expandedRows}
          onToggleExpand={handleRowExpand}
          onAssignClick={handleAssignClick}
          onAddTaskClick={handleAddTaskClick}
          onEditClick={(deal) => {
            setSelectedDeal(deal);
            setOpenEditModal(true);
          }}
          onDeleteClick={onDelete}
          onViewTermSheet={(deal, bankerId) => {
            setViewingDeal(deal);
            setViewingBankerId(bankerId);
          }}
          onClosingClick={openClosingDialog}
        />
      )}

      <AssignBankerDialog
        open={assignDialogOpen}
        deal={assigningDeal}
        bankers={bankers}
        onClose={() => setAssignDialogOpen(false)}
        onSave={handleAssignDeal}
        saving={savingAssignment}
      />

      <TaskFormDialog
        open={taskModal.open}
        deal={taskModal.deal as any}
        onClose={(action) => {
          setTaskModal({ open: false, deal: null });
          if (action === "reload") {
            setSnackbar({
              open: true,
              message: "Task added successfully.",
              severity: "success",
            });
          }
        }}
      />

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false, message: "" })}
      >
        <Alert
          severity={snackbar.severity}
          elevation={6}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Deal"
        description="Are you sure you want to delete this deal? This action cannot be undone."
      />

      <Dialog
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        maxWidth="md"
        fullWidth
        sx={{ "& .MuiDialog-paper": { overflow: "hidden", m: 0, p: 0 } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
          {selectedDeal ? "Edit Deal Request" : "Create New Deal Request"}
          <IconButton
            aria-label="close"
            onClick={() => setOpenEditModal(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ m: 0, p: 2, overflow: "hidden" }}>
          <EditLoanDeal
            customerId={selectedDeal?.customer?.id || loggedInUser?.id}
            dealId={selectedDeal?.id}
            onSuccess={() => {
              setOpenEditModal(false);
              loadDeals(searchQuery);
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!viewingDeal}
        onClose={() => setViewingDeal(null)}
        maxWidth="md"
        fullWidth
        sx={{ "& .MuiDialog-paper": { overflow: "hidden", m: 0, p: 0 } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
          Term Sheet Details
          <IconButton
            aria-label="close"
            onClick={() => setViewingDeal(null)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ m: 0, p: 2 }}>
          {viewingDeal ? (
            <TermSheetView deal={viewingDeal} bankerId={viewingBankerId} />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!closingDeal}
        onClose={closeClosingDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            mx: { xs: 2, sm: 0 },
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: { xs: 2, sm: 3 },
            py: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              minWidth: 0,
            }}
          >
            <EventAvailableIcon />
            <Typography
              component="span"
              sx={{
                fontWeight: 800,
                fontSize: { xs: 18, sm: 20 },
                lineHeight: 1.2,
              }}
            >
              Closing Date
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={closeClosingDialog}
            disabled={savingClosing}
            size="small"
            sx={{
              color: "primary.contrastText",
              border: "1px solid rgba(255,255,255,0.35)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: { xs: 2, sm: 3 },
            bgcolor: (theme) => theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 2,
              bgcolor: (theme) => theme.palette.action.hover,
            }}
          >
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: 12,
                fontWeight: 800,
                textTransform: "uppercase",
              }}
            >
              Deal
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                color: "text.primary",
                fontWeight: 800,
                fontSize: { xs: 16, sm: 17 },
                wordBreak: "break-word",
              }}
            >
              {closingDeal?.loanName || "Selected deal"}
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={1.25}
            alignItems="flex-start"
            sx={{ mt: 2.25 }}
          >
            <HelpOutlineIcon color="primary" sx={{ mt: 0.25 }} />
            <Box>
              <Typography sx={{ fontWeight: 800, color: "text.primary" }}>
                Select the closing date for this lead.
              </Typography>
              <Typography
                sx={{ mt: 0.5, color: "text.secondary", fontSize: 14 }}
              >
                The app will show a closing alert when this date is due.
              </Typography>
            </Box>
          </Stack>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Closing date"
              value={
                closingDate
                  ? parse(closingDate, "yyyy-MM-dd", new Date())
                  : null
              }
              onChange={(date) =>
                setClosingDate(date ? format(date, "yyyy-MM-dd") : "")
              }
              format="MM-dd-yyyy"
              minDate={new Date()}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "MM-DD-YYYY",
                  InputLabelProps: { shrink: true },
                  sx: { mt: 2.5 },
                  InputProps: {
                    sx: {
                      borderRadius: "10px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderRadius: "10px",
                      },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            gap: 1,
            flexDirection: { xs: "column-reverse", sm: "row" },
            "& .MuiButton-root": {
              width: { xs: "100%", sm: "auto" },
              borderRadius: 2,
              fontWeight: 700,
            },
          }}
        >
          <Button
            onClick={closeClosingDialog}
            disabled={savingClosing}
            variant="outlined"
            color="inherit"
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              savingClosing ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <EventRepeatIcon />
              )
            }
            onClick={saveClosingDate}
            disabled={savingClosing || !closingDate}
          >
            Save Date
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DealsPage;

